import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Form, FormStatus, FormTemplateType, QuestionView } from '../models';
import { FormApiService } from './form-api.service';
import { FormPersistenceService } from './form-persistence.service';
import { FormToRequestMapper } from '@formrails/shared/data-access';
import { FormStore } from '../store/form.store';

export interface FormSaveContext {
  formId: string;
  savedTemplateId: string | null;
  currentVersion: number;
  formVersion: number;
  formTitle: string;
  formDescription: string;
  showQuestionNumbers: boolean;
  questions: QuestionView[];
  /** Application templates only; see AssessmentMatricesComponent. */
  assessmentMatrixIds?: string[];
  /**
   * Set while an unsaved clone is open: the template it was copied from. Sent
   * with the create that first persists the clone, and not on later updates.
   */
  clonedFromId?: string | null;
  messageService: MessageService;
  /**
   * Which backend owns the template: 'Application' → Application API,
   * anything else → Forms API. Defaults to 'Application' when absent.
   */
  formType?: FormTemplateType;
}

export interface FormSaveResult {
  savedTemplateId: string;
  currentVersion: number;
}

/**
 * Service to handle form saving operations (draft, submit for approval, publish)
 */
@Injectable({
  providedIn: 'root'
})
export class FormSaveService {
  private formStore = inject(FormStore);
  private formApiService = inject(FormApiService);
  private formPersistenceService = inject(FormPersistenceService);

  /**
   * Save form as draft
   */
  saveDraft(
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    console.log('=== saveDraft() called ===');
    this.saveWithStatus(FormStatus.Draft, context, onSuccess);
  }

  /**
   * Publish form
   */
  publishForm(
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    console.log('=== publishForm() called ===');
    this.saveWithStatusPublish(context, onSuccess);
  }

  /**
   * Archive form
   */
  archiveForm(
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    console.log('=== archiveForm() called ===');
    this.saveWithStatus(FormStatus.Archived, context, onSuccess);
  }

  private saveWithStatus(
    status: FormStatus,
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    // Signal reads are synchronous snapshots — equivalent to the former combineLatest+take(1).
    const form = this.formStore.currentForm();
    const storeVersion = this.formStore.formVersion();
    const storeDescription = this.formStore.formDescription();
    const storeQuestions = this.formStore.questions();

    if (!form) {
      if (context.questions.length > 0 || context.formTitle) {
        const localForm: Form = {
          id: context.formId,
          title: context.formTitle || 'Untitled Form',
          status: status,
          showQuestionNumbers: context.showQuestionNumbers,
          version: context.formVersion || 0,
          description: context.formDescription || '',
          questions: context.questions,
          assessmentMatrixIds: context.assessmentMatrixIds ?? []
        };
        this.saveFormToApi(localForm, status, context, onSuccess);
      } else {
        console.error('Cannot save: no form data available');
      }
      return;
    }

    const versionToUse = this.determineVersion(context, storeVersion, form.version);

    const formToSave: Form = {
      ...form,
      version: versionToUse,
      description: storeDescription || form.description || '',
      questions: storeQuestions.length > 0 ? storeQuestions : context.questions,
      assessmentMatrixIds: context.assessmentMatrixIds ?? form.assessmentMatrixIds ?? []
    };

    this.formStore.updateFormStatus(status);
    this.saveFormToApi(formToSave, status, context, onSuccess);
  }

  private saveWithStatusPublish(
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    const form = this.formStore.currentForm();
    const storeVersion = this.formStore.formVersion();
    const storeDescription = this.formStore.formDescription();
    const storeQuestions = this.formStore.questions();

    if (!form) {
      console.error('No form to publish - form is null');
      return;
    }

    // Version is user-managed: publish with whatever the author set in the
    // header's version field (falling back through context/store), rather
    // than auto-incrementing.
    const versionToUse = this.determineVersion(context, storeVersion, form.version);

    const formToSave: Form = {
      ...form,
      version: versionToUse,
      description: storeDescription || form.description || '',
      questions: storeQuestions,
      assessmentMatrixIds: context.assessmentMatrixIds ?? form.assessmentMatrixIds ?? []
    };

    this.formStore.updateFormStatus(FormStatus.Published);
    this.saveFormToApi(formToSave, FormStatus.Published, context, onSuccess);
  }

  private determineVersion(context: FormSaveContext, storeVersion: number, formVersion?: number): number {
    if (context.formVersion > 0) return context.formVersion;
    if (context.currentVersion > 0) return context.currentVersion;
    if (storeVersion > 0) return storeVersion;
    if (formVersion && formVersion > 0) return formVersion;
    return 0;
  }

  private saveFormToApi(
    form: Form,
    status: FormStatus,
    context: FormSaveContext,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    try {
      const versionToMap = form.version ?? 0;
      const descriptionToMap = form.description || '';
      const questionsToUse = form.questions?.length > 0 ? form.questions : context.questions;

      const formWithCorrectVersion: Form = {
        ...form,
        version: versionToMap,
        description: descriptionToMap,
        questions: questionsToUse
      };

      const requestDto = FormToRequestMapper.toCreateDto(formWithCorrectVersion, versionToMap, descriptionToMap);
      // Map UI status to API status
      // Workflow: Draft -> Published (-> Archived)
      const statusMap: Record<FormStatus, 'Draft' | 'Published' | 'Archived'> = {
        [FormStatus.Archived]: 'Archived',
        [FormStatus.Draft]: 'Draft',
        [FormStatus.Published]: 'Published'
      };
      requestDto.status = statusMap[status] || 'Draft';

      const versionToSave = form.version ?? 0;
      const isUpdate = !!context.savedTemplateId;
      // Route by the context's type, falling back to the store's record of
      // which backend owns the template. Application → Application API,
      // everything else → Forms API.
      const formType = context.formType ?? this.formStore.formType() ?? 'Application';
      console.log(
        `[FormSave] Saving "${form.title}" (${formType}) via ${formType === 'Application' ? 'Application API' : 'Forms API'}`
      );

      if (context.savedTemplateId) {
        this.formApiService.updateApplicationTemplate(context.savedTemplateId, requestDto, formType).subscribe({
          next: (response) => {
            this.handleSaveSuccess(response.id, versionToSave, status, isUpdate, context.messageService, onSuccess);
          },
          error: (error) => {
            this.handleSaveError(error, status, isUpdate, context.messageService);
          }
        });
      } else {
        // Provenance is recorded once, by the create that first stores the clone.
        // The store is authoritative: it survives a refresh, the component does not.
        const clonedFromId = this.formStore.currentForm()?.clonedFromId ?? context.clonedFromId;
        if (clonedFromId) {
          requestDto.clonedFromId = clonedFromId;
        }
        this.formApiService.createApplicationTemplate(requestDto, formType).subscribe({
          next: (response) => {
            this.handleSaveSuccess(response.id, versionToSave, status, false, context.messageService, onSuccess);
          },
          error: (error) => {
            this.handleSaveError(error, status, false, context.messageService);
          }
        });
      }
    } catch (error) {
      console.error('Error in saveFormToApi:', error);
    }
  }

  private handleSaveSuccess(
    templateId: string,
    version: number,
    status: FormStatus,
    isUpdate: boolean,
    messageService: MessageService,
    onSuccess: (result: FormSaveResult) => void
  ): void {
    this.formStore.updateFormVersion(version);
    this.formPersistenceService.saveForm();

    const messages = this.getStatusMessages(status, true, isUpdate);
    messageService.add({
      severity: 'success',
      summary: messages.summary,
      detail: messages.detail,
      life: 3000
    });

    onSuccess({ savedTemplateId: templateId, currentVersion: version });
  }

  private handleSaveError(error: any, status: FormStatus, isUpdate: boolean, messageService: MessageService): void {
    console.error('Error saving:', error);
    const messages = this.getStatusMessages(status, false, isUpdate);
    messageService.add({
      severity: 'error',
      summary: messages.summary,
      detail: error.error?.detail || error.error?.message || error.message || messages.detail,
      life: 5000
    });
  }

  private getStatusMessages(status: FormStatus, isSuccess: boolean, isUpdate: boolean): { summary: string; detail: string } {
    if (isSuccess) {
      switch (status) {
        case FormStatus.Published:
          return {
            summary: 'Form Published',
            detail: isUpdate ? 'Your form has been published successfully.' : 'Your form has been created and published successfully.'
          };
        case FormStatus.Archived:
          return {
            summary: 'Form Archived',
            detail: 'Your form has been archived successfully.'
          };
        default:
          return {
            summary: isUpdate ? 'Draft Saved' : 'Form Created',
            detail: isUpdate ? 'Your draft has been saved successfully.' : 'Your form has been created successfully.'
          };
      }
    } else {
      switch (status) {
        case FormStatus.Published:
          return {
            summary: 'Publish Failed',
            detail: isUpdate ? 'Failed to publish form. Please try again.' : 'Failed to create and publish form. Please try again.'
          };
        case FormStatus.Archived:
          return {
            summary: 'Archive Failed',
            detail: 'Failed to archive form. Please try again.'
          };
        default:
          return {
            summary: isUpdate ? 'Save Failed' : 'Create Failed',
            detail: isUpdate ? 'Failed to save form. Please try again.' : 'Failed to create form. Please try again.'
          };
      }
    }
  }
}

